/**
 * Phase 2: UI to view and copy Mixpanel events (Route, Page Name, Event Name, Data).
 * Only rendered when VITE_MIXPANEL_CAPTURE_ENABLE=true (e.g. local/dev). Use "Copy as CSV" to paste into Notion.
 */

import { useState, useEffect, useCallback } from "react";
import { mixpanelCaptureEnable } from "common/config";
import type { MixpanelCaptureEntry } from "@/services/analytics";

const MAX_EVENTS = 100;

function escapeCsvCell(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}

function dataToCsvValue(data: Record<string, unknown>): string {
  const keys = Object.keys(data).filter(
    (k) => data[k] !== undefined && data[k] !== null && data[k] !== ""
  );
  if (keys.length === 0) return "";
  return keys.map((k) => `${k}: ${String(data[k])}`).join("; ");
}

export function MixpanelCaptureDevPanel() {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<MixpanelCaptureEntry[]>([]);

  const refresh = useCallback(() => {
    const capture = (window as Window & { __mixpanelCapture?: MixpanelCaptureEntry[] }).__mixpanelCapture;
    setEntries((Array.isArray(capture) ? capture : []).slice(-MAX_EVENTS));
  }, []);

  useEffect(() => {
    if (!mixpanelCaptureEnable) return;
    refresh();
    const interval = setInterval(refresh, 500);
    return () => clearInterval(interval);
  }, [refresh]);

  const copyAsCsv = useCallback(() => {
    const rows = [
      ["Mixpanel Key", "Route", "Page Name", "Event Name", "Data"],
      ...entries.map((e) => [
        e.mixpanelKey ?? "",
        e.route,
        e.pageName,
        e.eventName,
        dataToCsvValue(e.data as Record<string, unknown>),
      ]),
    ];
    const csv = rows.map((row) => row.map(escapeCsvCell).join(",")).join("\n");
    void navigator.clipboard.writeText(csv);
    setOpen(false);
  }, [entries]);

  const copyRowAsCsv = useCallback((e: MixpanelCaptureEntry) => {
    const row = [
      e.mixpanelKey ?? "",
      e.route,
      e.pageName,
      e.eventName,
      dataToCsvValue(e.data as Record<string, unknown>),
    ];
    const csv = row.map(escapeCsvCell).join(",");
    void navigator.clipboard.writeText(csv);
  }, []);

  const clearAll = useCallback(() => {
    const win = window as Window & { __mixpanelCapture?: MixpanelCaptureEntry[] };
    win.__mixpanelCapture = [];
    setEntries([]);
  }, []);

  if (!mixpanelCaptureEnable) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col items-end gap-2">
      {open && (
        <div
          className="flex max-h-[70vh] w-[min(90vw,640px)] flex-col rounded-lg border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-900"
          aria-label="Mixpanel capture panel"
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-gray-700">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Mixpanel events (last {entries.length})
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copyAsCsv}
                className="rounded bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700"
              >
                Copy as CSV
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600"
              >
                Close
              </button>
            </div>
          </div>
          <div className="overflow-auto max-h-[60vh]">
            <table className="w-full min-w-[900px] text-left text-xs">
              <thead className="sticky top-0 bg-gray-100 dark:bg-gray-800 z-10">
                <tr>
                  <th className="border-b p-2 font-medium whitespace-nowrap">Mixpanel Key</th>
                  <th className="border-b p-2 font-medium whitespace-nowrap">Route</th>
                  <th className="border-b p-2 font-medium whitespace-nowrap">Page Name</th>
                  <th className="border-b p-2 font-medium whitespace-nowrap">Event Name</th>
                  <th className="border-b p-2 font-medium whitespace-nowrap">Data</th>
                </tr>
              </thead>
              <tbody>
                {[...entries].reverse().map((e, i) => (
                  <tr
                    key={i}
                    role="button"
                    tabIndex={0}
                    onClick={() => copyRowAsCsv(e)}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        copyRowAsCsv(e);
                      }
                    }}
                    className="cursor-pointer border-b border-gray-100 transition-colors hover:bg-purple-50 dark:border-gray-700 dark:hover:bg-purple-900/20"
                    title="Click to copy row as CSV"
                  >
                    <td className="p-2 truncate max-w-[160px]" title={e.mixpanelKey ?? ""}>
                      {e.mixpanelKey ?? "—"}
                    </td>
                    <td className="p-2 truncate max-w-[140px]" title={e.route}>
                      {e.route}
                    </td>
                    <td className="p-2 truncate max-w-[120px]" title={e.pageName}>
                      {e.pageName}
                    </td>
                    <td className="p-2 truncate max-w-[200px]" title={e.eventName}>
                      {e.eventName}
                    </td>
                    <td className="p-2 truncate max-w-[280px]" title={dataToCsvValue(e.data as Record<string, unknown>)}>
                      {dataToCsvValue(e.data as Record<string, unknown>) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {entries.length === 0 && (
              <p className="p-4 text-center text-gray-500">No events captured yet. Click CTAs to record.</p>
            )}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded-full bg-purple-600 px-3 py-2 text-xs font-medium text-white shadow hover:bg-purple-700"
        aria-label={open ? "Close Mixpanel capture" : "Open Mixpanel capture"}
      >
        {open ? "Close" : "Mixpanel"}
      </button>
    </div>
  );
}
